// plan-13 S02: deterministic ghost-node roster — 47 ghosts per UTC minute

import { fromString, mulberry32 } from '../../core/seed'
import { ALIAS_POOL } from './aliases'
import { load, set } from '../../core/save'

export type GhostStatus = 'idle' | 'active' | 'broadcasting' | 'silent'

export type ActivityKind =
  | 'consensus' | 'divine' | 'decode' | 'paint'
  | 'tables' | 'log' | 'engage' | 'directory' | 'broadcast_relay'

export interface GhostActivity {
  kind: ActivityKind
  at: number // ms offset into the minute
  payload?: string
}

export interface GhostNode {
  alias: string
  status: GhostStatus
  lastActivity: GhostActivity
  nextEmitMs: number
}

const ACTIVITY_KINDS: ActivityKind[] = [
  'consensus', 'divine', 'decode', 'paint',
  'tables', 'log', 'engage', 'directory', 'broadcast_relay',
]

const CONSENSUS_WORDS = [
  'drift', 'king', 'node', 'signal', 'quorum',
  'gateway', 'relay', 'cursor', 'pattern', 'oracle',
  'consensus', 'frequency', 'proposal', 'hash', 'kernel',
]

export function getRoster(minuteKey: string, visitorAlias: string): GhostNode[] {
  const seed = fromString(minuteKey)
  const rng = mulberry32(seed)

  // pick 47 unique aliases, excluding visitor's alias
  const available = ALIAS_POOL.filter(a => a !== visitorAlias)
  const picked: string[] = []
  const used = new Set<string>()

  while (picked.length < 47 && used.size < available.length) {
    const idx = Math.floor(rng() * available.length)
    const alias = available[idx]
    if (!used.has(alias)) {
      used.add(alias)
      picked.push(alias)
    }
  }

  return picked.map(alias => {
    const r = rng()
    let status: GhostStatus
    if (r < 0.60) status = 'idle'
    else if (r < 0.90) status = 'active'
    else if (r < 0.95) status = 'broadcasting'
    else status = 'silent'

    const kind = ACTIVITY_KINDS[Math.floor(rng() * ACTIVITY_KINDS.length)]
    let payload: string | undefined
    if (kind === 'consensus') {
      payload = CONSENSUS_WORDS[Math.floor(rng() * CONSENSUS_WORDS.length)]
    } else if (kind === 'divine') {
      payload = generateFakeHash(rng)
    }

    const at = Math.floor(rng() * 60000)
    const nextEmitMs = Math.floor(rng() * 60000)

    return {
      alias,
      status,
      lastActivity: { kind, at, payload },
      nextEmitMs,
    }
  })
}

function generateFakeHash(rng: () => number): string {
  const chars = '0123456789abcdef'
  let h = ''
  for (let i = 0; i < 6; i++) h += chars[Math.floor(rng() * 16)]
  return h
}

export function getVisitorAlias(firstSeenAt: number): string {
  return ALIAS_POOL[firstSeenAt % ALIAS_POOL.length]
}

// ensure visitor alias is persisted
export function ensureVisitorAlias(): string {
  const s = load()
  if (s.nodeAlias) return s.nodeAlias
  const alias = getVisitorAlias(s.firstSeenAt)
  set('nodeAlias', alias)
  return alias
}
